import { useEffect, useState } from "react";

export const TopQuestionsTester: React.FC = () => {
    const [baseUrl, setBaseUrl] = useState<string>("https://www.sg.ch/steuern-finanzen/steuern");
    const [includeChildren, setIncludeChildren] = useState<boolean>(true);
    const [query, setQuery] = useState<string>("*");
    const [nofQuestions, setNofQuestions] = useState<number>(5);
    const [nofResultItemsToLoad, setNofResultItemsToLoad] = useState<number>(10);
    const [maxQuestionLength, setMaxQuestionLength] = useState<number>(50);
    const [questions, setQuestions] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [showInfoOverlay, setShowInfoOverlay] = useState<'baseUrl' | 'includeChildren' | 'query' | 'nofQuestions' | 'nofResultItemsToLoad' | 'maxQuestionLength' | null>(null);
    
    useEffect(() => {
        // Load Font Awesome
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css';
        document.head.appendChild(link);
        
        return () => {
            document.head.removeChild(link);
        };
    }, []);
    
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            const asyncFunc = async () => {
                setLoading(true);
                setError(null);
                try {
                    const response = await fetch(`/api/TopQuestions?baseUrl=${encodeURIComponent(baseUrl)}&includeChildren=${includeChildren}&query=${encodeURIComponent(query)}&nofQuestions=${nofQuestions}&nofResultItemsToLoad=${nofResultItemsToLoad}&maxQuestionLength=${maxQuestionLength}`, {
                        method: 'get',
                        headers: { 'Content-Type': 'application/json' }
                    });
                    
                    if (response.ok) {
                        const answer = await response.json();
                        console.log("answer", answer);
                        return answer;
                    } else {
                        const errorText = await response.text();
                        throw new Error(errorText || `Error: ${response.status}`);
                    }
                } catch (err) {
                    console.error(err);
                    throw err;
                }
            };
            
            asyncFunc().then(result => {
                setQuestions(result);
                setLoading(false);
            }).catch(err => {
                console.error(err);
                setError(err.message || err.toString());
                setLoading(false);
            });
        }, 400);
        
        return () => clearTimeout(timeoutId);
    }, [baseUrl, includeChildren, query, nofQuestions, nofResultItemsToLoad, maxQuestionLength]);

    return (
        <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', position: 'relative' }}>
            <h2 style={{ color: '#007a3d', borderBottom: '3px solid #007a3d', paddingBottom: '10px' }}>Test-Tool für Top-Fragen</h2>
            
            {/* Info Overlay */}
            {showInfoOverlay && (
                <>
                    <div 
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            zIndex: 999
                        }}
                        onClick={() => setShowInfoOverlay(null)}
                    ></div>
                    <div style={{
                        position: 'fixed',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: 'white',
                        padding: '30px',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                        maxWidth: '500px',
                        zIndex: 1000
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                            <h3 style={{ margin: 0, color: '#007a3d' }}>
                                {showInfoOverlay === 'baseUrl' && 'Basis-URL'}
                                {showInfoOverlay === 'includeChildren' && 'Suchbereich'}
                                {showInfoOverlay === 'query' && 'Suchbegriff'}
                                {showInfoOverlay === 'nofQuestions' && 'Anzahl Fragen'}
                                {showInfoOverlay === 'nofResultItemsToLoad' && 'Anzahl zu ladender Resultate'}
                                {showInfoOverlay === 'maxQuestionLength' && 'Maximale Fragenlänge'}
                            </h3>
                            <i 
                                className="fas fa-times" 
                                style={{ cursor: 'pointer', fontSize: '20px', color: '#666' }}
                                onClick={() => setShowInfoOverlay(null)}
                            ></i>
                        </div>
                        <p style={{ lineHeight: '1.6', color: '#333' }}>
                            {showInfoOverlay === 'baseUrl' && 'Die Basis-URL definiert den Bereich, aus dem die beliebtesten Inhalte ermittelt werden.'}
                            {showInfoOverlay === 'includeChildren' && 'Bestimmt ob nur die exakte Seite oder alle Unterseiten (gesamter Inhaltsbereich unterhalb der Basis-URL) berücksichtigt werden sollen.'}
                            {showInfoOverlay === 'query' && 'Der Suchbegriff filtert die Inhalte zusätzlich. Mit "*" werden alle Inhalte berücksichtigt.'}
                            {showInfoOverlay === 'nofQuestions' && 'Bestimmt die Anzahl der Fragen, die generiert werden sollen.'}
                            {showInfoOverlay === 'nofResultItemsToLoad' && 'Anzahl der Suchergebnisse, die aus dem Index geladen werden, um daraus Fragen zu generieren.'}
                            {showInfoOverlay === 'maxQuestionLength' && 'Maximale Länge einer generierten Frage in Zeichen.'}
                        </p>
                    </div>
                </>
            )}
            
            <div style={{ 
                backgroundColor: '#f5f5f5', 
                padding: '20px', 
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                marginBottom: '20px'
            }}>
                <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 'bold', marginBottom: '5px', color: '#333' }}>
                        <span>Basis-Seite:</span>
                        <i 
                            className="fas fa-info-circle" 
                            style={{ cursor: 'pointer', color: '#007a3d', fontSize: '16px' }}
                            onClick={() => setShowInfoOverlay('baseUrl')}
                            title="Info"
                        ></i>
                    </label>
                    <input 
                        type="text" 
                        value={baseUrl}
                        style={{ 
                            width: '100%', 
                            padding: '8px 12px',
                            border: '2px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '14px',
                            boxSizing: 'border-box'
                        }} 
                        onChange={(e) => {
                            let url = e.target.value;
                            if (url.endsWith('.html')) {
                                url = url.substring(0, url.length - 5);
                            }
                            setBaseUrl(url);
                        }}
                    />
                </div>

                <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 'bold', marginBottom: '5px', color: '#333' }}>
                        <span>Berücksichtigung:</span>
                        <i 
                            className="fas fa-info-circle" 
                            style={{ cursor: 'pointer', color: '#007a3d', fontSize: '16px' }}
                            onClick={() => setShowInfoOverlay('includeChildren')}
                            title="Info"
                        ></i>
                    </label>
                    <div style={{ display: 'flex', gap: '20px', marginTop: '8px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                            <input 
                                type="radio" 
                                name="includeChildren" 
                                checked={!includeChildren}
                                onChange={() => setIncludeChildren(false)}
                                style={{ marginRight: '5px' }}
                            />
                            <span>Nur die angegebene Seite</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                            <input 
                                type="radio" 
                                name="includeChildren" 
                                checked={includeChildren}
                                onChange={() => setIncludeChildren(true)}
                                style={{ marginRight: '5px' }}
                            />
                            <span>Inkl. Unterseiten</span>
                        </label>
                    </div>
                </div>

                <div style={{ display: 'flex', gap: '20px', marginBottom: '15px' }}>
                    <div style={{ flex: 1 }}>
                        <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 'bold', marginBottom: '5px', color: '#333' }}>
                            <span>Suchbegriff:</span>
                            <i 
                                className="fas fa-info-circle" 
                                style={{ cursor: 'pointer', color: '#007a3d', fontSize: '16px' }}
                                onClick={() => setShowInfoOverlay('query')}
                                title="Info"
                            ></i>
                        </label>
                        <input 
                            type="text" 
                            value={query}
                            style={{ 
                                width: '100%', 
                                padding: '8px 12px',
                                border: '2px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px',
                                boxSizing: 'border-box'
                            }} 
                            onChange={(e) => setQuery(e.target.value)}
                        />
                    </div>
                    <div style={{ flex: 1 }}>
                        <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 'bold', marginBottom: '5px', color: '#333' }}>
                            <span>Anzahl Fragen:</span>
                            <i 
                                className="fas fa-info-circle" 
                                style={{ cursor: 'pointer', color: '#007a3d', fontSize: '16px' }}
                                onClick={() => setShowInfoOverlay('nofQuestions')}
                                title="Info"
                            ></i>
                        </label>
                        <input 
                            type="number" 
                            value={nofQuestions}
                            style={{ 
                                width: '100%', 
                                padding: '8px 12px',
                                border: '2px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px',
                                boxSizing: 'border-box'
                            }} 
                            min={1} 
                            max={50}
                            onChange={(e) => setNofQuestions(parseInt(e.target.value) || 1)}
                        />
                    </div>
                </div>

                <div style={{ display: 'flex', gap: '20px', marginBottom: '15px' }}>
                    <div style={{ flex: 1 }}>
                        <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 'bold', marginBottom: '5px', color: '#333' }}>
                            <span>Anzahl zu ladender Resultate:</span>
                            <i 
                                className="fas fa-info-circle" 
                                style={{ cursor: 'pointer', color: '#007a3d', fontSize: '16px' }}
                                onClick={() => setShowInfoOverlay('nofResultItemsToLoad')}
                                title="Info"
                            ></i>
                        </label>
                        <input 
                            type="number" 
                            value={nofResultItemsToLoad}
                            style={{ 
                                width: '100%', 
                                padding: '8px 12px',
                                border: '2px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px',
                                boxSizing: 'border-box'
                            }} 
                            min={1} 
                            max={100}
                            onChange={(e) => setNofResultItemsToLoad(parseInt(e.target.value) || 10)}
                        />
                    </div>
                    <div style={{ flex: 1 }}>
                        <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 'bold', marginBottom: '5px', color: '#333' }}>
                            <span>Maximale Fragenlänge:</span>
                            <i 
                                className="fas fa-info-circle" 
                                style={{ cursor: 'pointer', color: '#007a3d', fontSize: '16px' }}
                                onClick={() => setShowInfoOverlay('maxQuestionLength')}
                                title="Info"
                            ></i>
                        </label>
                        <input 
                            type="number" 
                            value={maxQuestionLength}
                            style={{ 
                                width: '100%', 
                                padding: '8px 12px',
                                border: '2px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px',
                                boxSizing: 'border-box'
                            }} 
                            min={20} 
                            max={500}
                            onChange={(e) => setMaxQuestionLength(parseInt(e.target.value) || 100)}
                        />
                    </div>
                </div>
            </div>
            
            {/* Loading State */}
            {loading && (
                <div style={{ 
                    textAlign: 'center', 
                    padding: '40px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '8px'
                }}>
                    <i className="fas fa-spinner fa-spin" style={{ fontSize: '32px', color: '#007a3d' }}></i>
                    <p style={{ marginTop: '10px', color: '#666' }}>Lade Top-Fragen...</p>
                </div>
            )}
            
            {/* Error State */}
            {error && (
                <div style={{ 
                    padding: '20px',
                    backgroundColor: '#fee',
                    border: '2px solid #fcc',
                    borderRadius: '8px',
                    color: '#c00'
                }}>
                    <i className="fas fa-exclamation-triangle" style={{ marginRight: '8px' }}></i>
                    <strong>Fehler:</strong> {error}
                </div>
            )}
            
            {/* Results */}
            {!loading && !error && questions && questions.length > 0 && (
                <div>
                    <h3 style={{ color: '#007a3d', marginBottom: '15px' }}>
                        Generierte Fragen ({questions.length}):
                    </h3>
                    <div style={{ 
                        backgroundColor: 'white',
                        border: '2px solid #ddd',
                        borderRadius: '8px',
                        padding: '20px'
                    }}>
                        <ol style={{ 
                            margin: 0, 
                            paddingLeft: '20px',
                            lineHeight: '1.8'
                        }}>
                            {questions.map((question, index) => (
                                <li key={index} style={{ 
                                    marginBottom: '12px',
                                    padding: '10px',
                                    backgroundColor: index % 2 === 0 ? '#f9f9f9' : 'white',
                                    borderRadius: '4px',
                                    borderLeft: '3px solid #007a3d'
                                }}>
                                    <a 
                                        href={`/?bypassTestPhaseExpiredCheck&autoRun=true&q=${encodeURIComponent(question)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            color: '#007a3d',
                                            textDecoration: 'none',
                                            fontWeight: '500'
                                        }}
                                        onMouseOver={(e) => e.currentTarget.style.textDecoration = 'underline'}
                                        onMouseOut={(e) => e.currentTarget.style.textDecoration = 'none'}
                                    >
                                        {question}
                                    </a>
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>
            )}
            
            {/* No Results */}
            {!loading && !error && questions && questions.length === 0 && (
                <div style={{ 
                    textAlign: 'center', 
                    padding: '40px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '8px',
                    color: '#666'
                }}>
                    <i className="fas fa-question-circle" style={{ fontSize: '48px', marginBottom: '15px', opacity: 0.5 }}></i>
                    <p>Keine Fragen gefunden.</p>
                </div>
            )}
        </div>
    );
};
